import { z } from 'zod';
import { billingIntervalSchema, discountCodeSchema, invoiceStatusSchema, planIdSchema, pricingTypeSchema, refundRequestStatusSchema, } from '../billing.js';
import { handoffReasonSchema, markKindSchema, messageRoleSchema } from '../conversations.js';
/**
 * The admin panel's API (`apps/admin`, admin.<domain>): Groundly Admins only,
 * with their own session (`modules/admin`). Read-mostly views across every
 * account, and the billing actions the `admin:billing` command offers, each
 * recorded in the admin audit log under the signed-in admin's name.
 */
const none = z.object({});
const isoDate = z.iso.date();
const isoTime = z.iso.datetime();
const page = z.coerce.number().int().min(1).default(1);
/** Millionths of a US dollar, as `model_calls` stores costs. */
const micro = z.number();
// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------
export const adminSchema = z.object({ id: z.uuid(), email: z.string(), name: z.string() });
export const adminSignIn = {
    method: 'POST',
    path: '/api/admin/session',
    params: none,
    query: none,
    body: z.object({
        email: z.string().trim().toLowerCase().min(3).max(320),
        password: z.string().min(1).max(200),
        /** The 6-digit code from the authenticator app. */
        code: z.string().trim().min(6).max(8),
    }),
    response: adminSchema,
};
export const adminSignOut = {
    method: 'DELETE',
    path: '/api/admin/session',
    params: none,
    query: none,
    body: none,
    response: z.void(),
};
export const getAdminMe = {
    method: 'GET',
    path: '/api/admin/me',
    params: none,
    query: none,
    body: none,
    response: adminSchema,
};
// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------
/** One website as the lists show it. */
export const adminWebsiteRowSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    domains: z.array(z.string()),
    accountId: z.uuid(),
    ownerEmail: z.string().nullable(),
    industry: z.string(),
    planId: planIdSchema.nullable(),
    planName: z.string().nullable(),
    /** AI replies counted this cycle. */
    used: z.number().int(),
    /** This cycle's included AI replies, prorated; null without a plan. */
    included: z.number().int().nullable(),
    costMicroUsd: micro,
    approvedPages: z.number().int(),
    indexedPages: z.number().int(),
    paused: z.boolean(),
    deleted: z.boolean(),
    createdAt: isoTime,
});
/** Model-call statistics over a period, for a website or for everything. */
export const adminAiStatsSchema = z.object({
    /** Assistant answers given (model-written or not). */
    replies: z.number().int(),
    /** Answers that made at least one model call. */
    modelReplies: z.number().int(),
    calls: z.number().int(),
    toolRounds: z.number().int(),
    failedCalls: z.number().int(),
    rejected: z.number().int(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    costMicroUsd: micro,
    /** Average estimated input tokens per call, by part. */
    avgParts: z.object({
        guardrail: z.number(),
        tools: z.number(),
        content: z.number(),
        history: z.number(),
        question: z.number(),
        toolResult: z.number(),
        overhead: z.number(),
    }),
    latencyP50Ms: z.number().nullable(),
    latencyP95Ms: z.number().nullable(),
    /** Answers by outcome: `answered`, or the handoff reason. */
    outcomes: z.array(z.object({ outcome: z.string(), count: z.number().int() })),
});
const daySchema = z.object({
    date: isoDate,
    replies: z.number().int(),
    costMicroUsd: micro,
});
// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
export const adminOverviewSchema = z.object({
    periodStart: isoDate,
    periodEnd: isoDate,
    accounts: z.number().int(),
    websites: z.number().int(),
    websitesByPlan: z.array(z.object({ planId: z.string(), count: z.number().int() })),
    /** AI replies counted for billing this cycle, across every website. */
    billedReplies: z.number().int(),
    /** The cycle's AI cost so far, from `model_calls`. */
    aiCostMicroUsd: micro,
    /** Answers that made a model call this cycle. */
    modelReplies: z.number().int(),
    /** Plan prices this cycle (founding or list), before overage and discounts. */
    planRevenueCents: z.number().int(),
    /** Websites at 80% or more of this cycle's included replies, most used first. */
    nearCap: z.array(z.object({
        websiteId: z.uuid(),
        websiteName: z.string(),
        planName: z.string(),
        used: z.number().int(),
        included: z.number().int(),
        /** A Free website past its replies: the assistant isn't answering. */
        stopped: z.boolean(),
    })),
    /** Approved pages not indexed yet. */
    indexingBacklog: z.number().int(),
    failedJobs24h: z.number().int(),
    unpaidInvoices: z.number().int(),
    /** The last 30 days. */
    days: z.array(daySchema),
});
export const getAdminOverview = {
    method: 'GET',
    path: '/api/admin/overview',
    params: none,
    query: none,
    body: none,
    response: adminOverviewSchema,
};
// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
export const listAdminAccounts = {
    method: 'GET',
    path: '/api/admin/accounts',
    params: none,
    query: z.object({ q: z.string().trim().max(200).optional(), page }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            ownerEmail: z.string().nullable(),
            users: z.number().int(),
            websites: z.number().int(),
            createdAt: isoTime,
        })),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
    }),
};
export const getAdminAccount = {
    method: 'GET',
    path: '/api/admin/accounts/:accountId',
    params: z.object({ accountId: z.uuid() }),
    query: none,
    body: none,
    response: z.object({
        id: z.uuid(),
        createdAt: isoTime,
        users: z.array(z.object({
            id: z.uuid(),
            email: z.string(),
            name: z.string().nullable(),
            isSuperUser: z.boolean(),
            emailVerified: z.boolean(),
            removed: z.boolean(),
            createdAt: isoTime,
        })),
        websites: z.array(adminWebsiteRowSchema),
    }),
};
// ---------------------------------------------------------------------------
// Websites
// ---------------------------------------------------------------------------
export const listAdminWebsites = {
    method: 'GET',
    path: '/api/admin/websites',
    params: none,
    query: z.object({
        q: z.string().trim().max(200).optional(),
        plan: z.union([planIdSchema, z.literal('none')]).optional(),
        page,
    }),
    body: none,
    response: z.object({
        items: z.array(adminWebsiteRowSchema),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
    }),
};
const websiteParams = z.object({ websiteId: z.uuid() });
export const adminWebsiteSchema = z.object({
    website: adminWebsiteRowSchema.extend({
        websiteKey: z.string(),
        setupStep: z.number().int(),
    }),
    subscription: z
        .object({
        planId: planIdSchema,
        pricingType: pricingTypeSchema,
        billingInterval: billingIntervalSchema,
        startsOn: isoDate,
        endsOn: isoDate.nullable(),
        foundingLockedUntil: isoDate.nullable(),
        /** A yearly plan: the cycle its fee is next charged in. */
        renewsOn: isoDate.nullable(),
    })
        .nullable(),
    /** A change already set for the next cycle, or for a yearly plan's renewal. */
    nextSubscription: z
        .object({
        planId: planIdSchema,
        pricingType: pricingTypeSchema,
        billingInterval: billingIntervalSchema,
        startsOn: isoDate,
    })
        .nullable(),
    usage: z.object({
        periodStart: isoDate,
        periodEnd: isoDate,
        used: z.number().int(),
        included: z.number().int().nullable(),
        projected: z.number().int(),
        estimatedOverageCents: z.number().int(),
    }),
    knowledge: z.object({
        pages: z.number().int(),
        approved: z.number().int(),
        indexed: z.number().int(),
        /** Approved, not indexed yet. */
        waiting: z.number().int(),
        /** Whose last capture failed. */
        failedCaptures: z.number().int(),
        chunks: z.number().int(),
        chunkTokens: z.number().int(),
        directoryChunks: z.number().int(),
        customPages: z.number().int(),
        approvalsThisCycle: z.number().int(),
        approvalLimit: z.number().int().nullable(),
        customPageLimit: z.number().int().nullable(),
        lastCrawl: z
            .object({
            status: z.string(),
            pagesFound: z.number().int(),
            pagesFailed: z.number().int(),
            createdAt: isoTime,
            completedAt: isoTime.nullable(),
            failureReason: z.string().nullable(),
        })
            .nullable(),
    }),
    /** The last 30 days. */
    ai: adminAiStatsSchema,
    recentConversations: z.array(z.object({
        id: z.uuid(),
        startedAt: isoTime,
        firstVisitorMessage: z.string().nullable(),
        messages: z.number().int(),
        costMicroUsd: micro,
        handoffs: z.number().int(),
    })),
});
export const getAdminWebsite = {
    method: 'GET',
    path: '/api/admin/websites/:websiteId',
    params: websiteParams,
    query: none,
    body: none,
    response: adminWebsiteSchema,
};
/** What an admin action answers: the line the audit log records. */
const actionResult = z.object({ result: z.string() });
export const pauseAdminWebsite = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/pause',
    params: websiteParams,
    query: none,
    body: none,
    response: actionResult,
};
export const resumeAdminWebsite = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/resume',
    params: websiteParams,
    query: none,
    body: none,
    response: actionResult,
};
export const reindexAdminWebsite = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/reindex',
    params: websiteParams,
    query: none,
    body: none,
    response: actionResult,
};
export const testAdminQuestion = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/test-question',
    params: websiteParams,
    query: none,
    body: z.object({
        question: z.string().trim().min(1).max(2000),
        /** Also ask the model, which costs a model call; otherwise retrieval only. */
        answer: z.boolean().default(false),
    }),
    response: z.object({
        strongMatch: z.boolean(),
        sources: z.array(z.object({
            id: z.string(),
            kind: z.string(),
            pageType: z.string().nullable(),
            title: z.string(),
            url: z.string().nullable(),
            tokenCount: z.number().int(),
            text: z.string(),
        })),
        contentTokens: z.number().int(),
        answer: z
            .object({
            content: z.string(),
            outcome: z.string(),
            handoffReason: z.string().nullable(),
            sources: z.array(z.object({ pageTitle: z.string(), url: z.string() })),
            calls: z.array(z.object({
                kind: z.string(),
                inputTokens: z.number().int(),
                outputTokens: z.number().int(),
                costMicroUsd: micro,
                latencyMs: z.number().int(),
                error: z.string().nullable(),
            })),
        })
            .nullable(),
    }),
};
// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------
export const setAdminWebsitePlan = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/plan',
    params: websiteParams,
    query: none,
    body: z.object({
        planId: planIdSchema,
        pricingType: pricingTypeSchema,
        /** Left out, the website keeps how it pays now (monthly for a first plan). */
        billingInterval: billingIntervalSchema.optional(),
        startsOn: isoDate.optional(),
    }),
    response: actionResult,
};
export const endAdminWebsitePlan = {
    method: 'POST',
    path: '/api/admin/websites/:websiteId/end-plan',
    params: websiteParams,
    query: none,
    body: none,
    response: actionResult,
};
export const listAdminInvoices = {
    method: 'GET',
    path: '/api/admin/invoices',
    params: none,
    query: z.object({ status: invoiceStatusSchema.optional() }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            number: z.number().int(),
            label: z.string(),
            accountId: z.uuid(),
            accountEmail: z.string(),
            periodStart: isoDate,
            periodEnd: isoDate,
            status: invoiceStatusSchema,
            totalCents: z.number().int(),
            websiteCount: z.number().int(),
            issuedAt: isoTime,
            paidAt: isoTime.nullable(),
            paymentReference: z.string().nullable(),
        })),
    }),
};
// -- 14-day yearly refunds ---------------------------------------------------
/**
 * Customers' requests to cancel a new yearly plan within 14 days of paying
 * and get the payment back: pending first. Approving one ends the plan at
 * once (the website moves to Free); the payment itself is refunded in Paddle.
 */
export const listAdminRefundRequests = {
    method: 'GET',
    path: '/api/admin/refund-requests',
    params: none,
    query: none,
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            accountId: z.uuid(),
            accountEmail: z.string(),
            websiteId: z.uuid(),
            websiteName: z.string(),
            invoiceLabel: z.string(),
            amountCents: z.number().int(),
            status: refundRequestStatusSchema,
            requestedAt: isoTime,
            decidedBy: z.string().nullable(),
            decidedAt: isoTime.nullable(),
        })),
    }),
};
const refundRequestParams = z.object({ refundRequestId: z.uuid() });
export const approveAdminRefundRequest = {
    method: 'POST',
    path: '/api/admin/refund-requests/:refundRequestId/approve',
    params: refundRequestParams,
    query: none,
    body: none,
    response: actionResult,
};
export const declineAdminRefundRequest = {
    method: 'POST',
    path: '/api/admin/refund-requests/:refundRequestId/decline',
    params: refundRequestParams,
    query: none,
    body: none,
    response: actionResult,
};
const invoiceParams = z.object({ number: z.coerce.number().int().min(1) });
export const markAdminInvoicePaid = {
    method: 'POST',
    path: '/api/admin/invoices/:number/paid',
    params: invoiceParams,
    query: none,
    body: z.object({ reference: z.string().trim().max(200).optional() }),
    response: actionResult,
};
export const markAdminInvoiceUnpaid = {
    method: 'POST',
    path: '/api/admin/invoices/:number/unpaid',
    params: invoiceParams,
    query: none,
    body: none,
    response: actionResult,
};
export const voidAdminInvoice = {
    method: 'POST',
    path: '/api/admin/invoices/:number/void',
    params: invoiceParams,
    query: none,
    body: none,
    response: actionResult,
};
export const generateAdminInvoices = {
    method: 'POST',
    path: '/api/admin/invoices/generate',
    params: none,
    query: none,
    body: none,
    response: actionResult,
};
export const listAdminDiscounts = {
    method: 'GET',
    path: '/api/admin/discounts',
    params: none,
    query: none,
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            accountId: z.uuid(),
            accountEmail: z.string(),
            label: z.string(),
            description: z.string(),
            websiteNames: z.array(z.string()).nullable(),
            /** The code it was redeemed from, or null when an admin gave it. */
            code: z.string().nullable(),
            startsOn: isoDate,
            endsOn: isoDate.nullable(),
            createdBy: z.string(),
            createdAt: isoTime,
        })),
    }),
};
export const addAdminDiscount = {
    method: 'POST',
    path: '/api/admin/discounts',
    params: none,
    query: none,
    body: z
        .object({
        /** The account, by id or by an Account User's email. */
        account: z.string().trim().min(1).max(320).optional(),
        websiteIds: z.array(z.uuid()).max(50).default([]),
        label: z.string().trim().min(1).max(120),
        percentOff: z.number().int().min(1).max(100).optional(),
        amountOffCents: z.number().int().min(1).optional(),
        startsOn: isoDate.optional(),
        cycles: z.number().int().min(1).max(120).optional(),
        until: isoDate.optional(),
    })
        .refine((d) => (d.percentOff === undefined) !== (d.amountOffCents === undefined), {
        message: 'Give either a percentage or an amount.',
        path: ['percentOff'],
    }),
    response: actionResult,
};
export const endAdminDiscount = {
    method: 'POST',
    path: '/api/admin/discounts/:discountId/end',
    params: z.object({ discountId: z.uuid() }),
    query: none,
    body: z.object({ now: z.boolean().default(false) }),
    response: actionResult,
};
export const listAdminDiscountCodes = {
    method: 'GET',
    path: '/api/admin/discount-codes',
    params: none,
    query: none,
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            code: z.string(),
            label: z.string(),
            description: z.string(),
            /** "3 cycles", "until 2027-03-04" or "until ended": how long each redemption lasts. */
            lasts: z.string(),
            maxRedemptions: z.number().int().nullable(),
            redemptions: z.number().int(),
            expiresOn: isoDate.nullable(),
            oncePerAccount: z.boolean(),
            endedAt: isoTime.nullable(),
            createdBy: z.string(),
            createdAt: isoTime,
        })),
    }),
};
export const addAdminDiscountCode = {
    method: 'POST',
    path: '/api/admin/discount-codes',
    params: none,
    query: none,
    body: z
        .object({
        code: discountCodeSchema,
        label: z.string().trim().min(1).max(120),
        percentOff: z.number().int().min(1).max(100).optional(),
        amountOffCents: z.number().int().min(1).optional(),
        /** How long each redemption lasts: cycles, or until a date's cycle; neither is until ended. */
        cycles: z.number().int().min(1).max(120).optional(),
        until: isoDate.optional(),
        maxRedemptions: z.number().int().min(1).optional(),
        /** The last day the code can be entered. */
        expiresOn: isoDate.optional(),
        oncePerAccount: z.boolean().default(false),
    })
        .refine((d) => (d.percentOff === undefined) !== (d.amountOffCents === undefined), {
        message: 'Give either a percentage or an amount.',
        path: ['percentOff'],
    })
        .refine((d) => d.cycles === undefined || d.until === undefined, {
        message: 'Give cycles or an end date, not both.',
        path: ['cycles'],
    }),
    response: actionResult,
};
/** No one can enter the code after this; discounts already redeemed carry on. */
export const endAdminDiscountCode = {
    method: 'POST',
    path: '/api/admin/discount-codes/:codeId/end',
    params: z.object({ codeId: z.uuid() }),
    query: none,
    body: none,
    response: actionResult,
};
// ---------------------------------------------------------------------------
// AI cost and quality
// ---------------------------------------------------------------------------
export const ADMIN_PERIODS = [7, 30, 90];
const periodQuery = z.object({
    days: z.coerce
        .number()
        .int()
        .refine((d) => ADMIN_PERIODS.includes(d))
        .default(30),
});
export const getAdminAi = {
    method: 'GET',
    path: '/api/admin/ai',
    params: none,
    query: periodQuery,
    body: none,
    response: z.object({
        days: z.number().int(),
        totals: adminAiStatsSchema,
        websites: z.array(z.object({
            websiteId: z.uuid(),
            websiteName: z.string(),
            replies: z.number().int(),
            modelReplies: z.number().int(),
            costMicroUsd: micro,
            toolRounds: z.number().int(),
            rejected: z.number().int(),
            avgInputTokens: z.number(),
        })),
        series: z.array(daySchema),
    }),
};
export const listAdminRejected = {
    method: 'GET',
    path: '/api/admin/ai/rejected',
    params: none,
    query: z.object({ page }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            messageId: z.uuid().nullable(),
            conversationId: z.uuid(),
            websiteName: z.string(),
            createdAt: isoTime,
            question: z.string().nullable(),
            why: z.string(),
            output: z.unknown(),
        })),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
    }),
};
export const listAdminUnanswered = {
    method: 'GET',
    path: '/api/admin/ai/unanswered',
    params: none,
    query: periodQuery,
    body: none,
    response: z.object({
        items: z.array(z.object({
            question: z.string(),
            websiteId: z.uuid(),
            websiteName: z.string(),
            count: z.number().int(),
            lastAskedAt: isoTime,
            conversationId: z.uuid(),
        })),
    }),
};
/**
 * Answers Account Users marked as invalid or incorrect, with their feedback
 * (`product/business-rules.md`, Marks and feedback): the ones to review first,
 * then the reviewed ones.
 */
export const listAdminMarks = {
    method: 'GET',
    path: '/api/admin/marks',
    params: none,
    query: z.object({ status: z.enum(['open', 'reviewed']).default('open'), page }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            kind: markKindSchema,
            feedback: z.string(),
            question: z.string().nullable(),
            answer: z.string(),
            websiteId: z.uuid(),
            websiteName: z.string(),
            accountEmail: z.string(),
            conversationId: z.uuid(),
            markedBy: z.string(),
            createdAt: isoTime,
            reviewedAt: isoTime.nullable(),
            reviewedBy: z.string().nullable(),
        })),
        total: z.number().int(),
    }),
};
export const reviewAdminMark = {
    method: 'POST',
    path: '/api/admin/marks/:markId/reviewed',
    params: z.object({ markId: z.uuid() }),
    query: none,
    body: none,
    response: actionResult,
};
// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------
export const listAdminConversations = {
    method: 'GET',
    path: '/api/admin/conversations',
    params: none,
    query: z.object({
        websiteId: z.uuid().optional(),
        reason: handoffReasonSchema.optional(),
        page,
    }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            websiteId: z.uuid(),
            websiteName: z.string(),
            startedAt: isoTime,
            firstVisitorMessage: z.string().nullable(),
            messages: z.number().int(),
            handoffs: z.number().int(),
            costMicroUsd: micro,
        })),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
    }),
};
export const getAdminConversation = {
    method: 'GET',
    path: '/api/admin/conversations/:conversationId',
    params: z.object({ conversationId: z.uuid() }),
    query: none,
    body: none,
    response: z.object({
        id: z.uuid(),
        websiteId: z.uuid(),
        websiteName: z.string(),
        accountId: z.uuid(),
        startedAt: isoTime,
        endedAt: isoTime.nullable(),
        hasLead: z.boolean(),
        costMicroUsd: micro,
        messages: z.array(z.object({
            id: z.uuid(),
            role: messageRoleSchema,
            content: z.string(),
            outcome: z.string().nullable(),
            handoffReason: z.string().nullable(),
            sources: z.unknown(),
            createdAt: isoTime,
            calls: z.array(z.object({
                kind: z.string(),
                attempt: z.number().int(),
                model: z.string(),
                guardrailTokens: z.number().int(),
                toolsTokens: z.number().int(),
                contentTokens: z.number().int(),
                historyTokens: z.number().int(),
                questionTokens: z.number().int(),
                toolResultTokens: z.number().int(),
                overheadTokens: z.number().int(),
                inputTokens: z.number().int(),
                outputTokens: z.number().int(),
                costMicroUsd: micro,
                latencyMs: z.number().int(),
                error: z.string().nullable(),
                rejectedWhy: z.string().nullable(),
            })),
        })),
    }),
};
// ---------------------------------------------------------------------------
// Audit and system
// ---------------------------------------------------------------------------
export const listAdminAudit = {
    method: 'GET',
    path: '/api/admin/audit',
    params: none,
    query: z.object({ page }),
    body: none,
    response: z.object({
        items: z.array(z.object({
            id: z.uuid(),
            admin: z.string(),
            command: z.string(),
            arguments: z.record(z.string(), z.unknown()),
            result: z.string(),
            createdAt: isoTime,
        })),
        total: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
    }),
};
export const getAdminSystem = {
    method: 'GET',
    path: '/api/admin/system',
    params: none,
    query: none,
    body: none,
    response: z.object({
        now: isoTime,
        queues: z.array(z.object({
            name: z.string(),
            waiting: z.number().int(),
            active: z.number().int(),
            failed24h: z.number().int(),
            completed24h: z.number().int(),
            lastCompletedAt: isoTime.nullable(),
        })),
        recentFailures: z.array(z.object({ queue: z.string(), failedAt: isoTime, error: z.string().nullable() })),
        indexing: z.object({ backlog: z.number().int(), oldestWaitingSince: isoTime.nullable() }),
        databaseBytes: z.number(),
        adminSessions: z.number().int(),
    }),
};
//# sourceMappingURL=admin.js.map